import { useState, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  ColumnDef,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Checkbox,
  Alert,
  Collapse,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { EnvelopeSimple } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import MDButton from 'src/components/md/MDButton';
import Modal from 'src/components/modal/Modal';
import ProductV2Pill from 'src/components/products/ProductV2Pill';
import SalesOrderDraftModal from 'src/pages/authenticated/core/sales-order-drafts/components/sales-order-draft-modal/SalesOrderDraftModal';
import { CustomerPill } from 'src/components/customer/CustomerPill';
import { SalesOrderDTO } from 'src/api/accounts-receivable/sales-orders/useSalesOrder';
import { SalesOrderSource } from '@erp/shared-types';
import useUpdateSalesOrderLineItem from 'src/api/accounts-receivable/sales-orders/useUpdateSalesOrderLineItem';
import * as Sentry from '@sentry/react';
import useRetrySendEdiAck from 'src/api/accounts-receivable/sales-orders/useRetrySendEdiAck';
import ApiError from 'src/api/ApiError';

// Toggle this flag to show Famous sync status view instead of normal sales order view
const SHOW_FAMOUS_SYNC = true;

type SalesOrderLineItem = SalesOrderDTO['salesOrderLineItems'][number];

interface Props {
  salesOrder: SalesOrderDTO;
  editable?: boolean;
  onUpdate?: () => void;
}

// Edit field modal
interface EditFieldModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (value: string | number, sendAcknowledgment: boolean) => void;
  lineItem: SalesOrderLineItem;
  field: 'quantity' | 'unitPrice' | 'rescheduledDate';
  currentValue: string | number | null;
  isLoading?: boolean;
  error?: ApiError | null;
  salesOrder: SalesOrderDTO;
}

const EditFieldModal: React.FC<EditFieldModalProps> = ({
  open,
  onClose,
  onConfirm,
  lineItem,
  field,
  currentValue,
  isLoading = false,
  error,
  salesOrder,
}) => {
  const [value, setValue] = useState('');
  const [sendAcknowledgment, setSendAcknowledgment] = useState(true);

  useEffect(() => {
    if (open) {
      if (
        field === 'rescheduledDate' &&
        currentValue &&
        typeof currentValue === 'string'
      ) {
        // For date fields, format as YYYY-MM-DD
        setValue(currentValue.split('T')[0]);
      } else {
        setValue(currentValue?.toString() || '');
      }
    }
  }, [open, currentValue, field]);

  const handleConfirm = () => {
    let processedValue: string | number = value;

    if (field === 'quantity' || field === 'unitPrice') {
      processedValue = parseFloat(value);
      if (isNaN(processedValue) || processedValue <= 0) {
        alert('Please enter a valid positive number');
        return;
      }
    } else if (field === 'rescheduledDate') {
      if (!value.match(/^\d{4}-\d{2}-\d{2}$/)) {
        alert('Please enter a valid date (YYYY-MM-DD)');
        return;
      }
    }

    onConfirm(processedValue, sendAcknowledgment);
  };

  const getFieldLabel = () => {
    switch (field) {
      case 'quantity':
        return 'Quantity';
      case 'unitPrice':
        return 'Unit Price';
      case 'rescheduledDate':
        return 'Delivery Date';
      default:
        return field;
    }
  };

  const getInputType = () => {
    switch (field) {
      case 'quantity':
        return 'number';
      case 'unitPrice':
        return 'number';
      case 'rescheduledDate':
        return 'date';
      default:
        return 'text';
    }
  };

  // Determine acknowledgment method based on sales order source (exhaustive check)
  const getAcknowledgmentMethod = (): string => {
    switch (salesOrder.source) {
      case SalesOrderSource.Edi:
        return 'EDI';
      case SalesOrderSource.Email:
      case SalesOrderSource.DirectEntry:
      case SalesOrderSource.FileUpload:
        return 'email';
      default: {
        // Exhaustiveness check - will cause TS error if a new source is added
        const exhaustiveCheck: never = salesOrder.source;
        const error = new Error(
          `Unhandled SalesOrderSource: ${exhaustiveCheck}`,
        );
        console.error(error);
        Sentry.captureException(error);
        return 'email'; // Fallback to email
      }
    }
  };

  return (
    <Modal
      open={open}
      title={`Edit ${getFieldLabel()}`}
      closeable
      maxWidth="sm"
      isLoading={isLoading}
      error={error}
      onCloseButton={{
        text: 'Cancel',
        onClick: onClose,
        disabled: isLoading,
      }}
      onConfirmButton={{
        text: isLoading ? 'Saving...' : 'Save Changes',
        onClick: handleConfirm,
        disabled: isLoading,
      }}
    >
      <MDBox p={2}>
        <MDBox
          mb={3}
          p={2}
          sx={{
            backgroundColor: '#f0f4f8',
            borderRadius: 1,
            border: '1px solid #e1e8ed',
          }}
        >
          <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
            Editing Line Item:
          </MDTypography>
          <MDBox display="flex" alignItems="center" gap={2}>
            {/* Product Info */}
            {lineItem.salesOrderItemType ===
              'customer_purchase_order_line_item' &&
            lineItem.customerPurchaseOrderLineItemId ? (
              <MDBox>
                <MDTypography variant="caption" color="text.secondary">
                  Product from PO Line Item
                </MDTypography>
              </MDBox>
            ) : lineItem.productId && lineItem.productVariantId ? (
              <ProductV2Pill
                productId={lineItem.productId}
                productVariantId={lineItem.productVariantId}
              />
            ) : (
              <MDTypography variant="caption" color="text.secondary">
                Product info not available
              </MDTypography>
            )}

            {/* Current values */}
            <MDBox display="flex" gap={3} ml="auto">
              <MDBox>
                <MDTypography variant="caption" color="text.secondary">
                  Qty: {lineItem.quantity || '-'}
                </MDTypography>
              </MDBox>
              <MDBox>
                <MDTypography variant="caption" color="text.secondary">
                  Price:{' '}
                  {lineItem.unitPrice
                    ? `$${Number(lineItem.unitPrice).toFixed(2)}`
                    : '-'}
                </MDTypography>
              </MDBox>
              <MDBox>
                <MDTypography variant="caption" color="text.secondary">
                  Delivery:{' '}
                  {lineItem.rescheduledDate
                    ? new Date(lineItem.rescheduledDate).toLocaleDateString()
                    : '-'}
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        </MDBox>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <MDTypography variant="body2">
              Failed to update {getFieldLabel().toLowerCase()}:{' '}
              {error.message || 'An error occurred'}
            </MDTypography>
          </Alert>
        )}

        <TextField
          label={getFieldLabel()}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          type={getInputType()}
          fullWidth
          margin="normal"
          autoFocus
          disabled={isLoading}
          inputProps={{
            step: field === 'unitPrice' ? '0.01' : undefined,
            min:
              field === 'quantity' || field === 'unitPrice'
                ? '0.01'
                : undefined,
          }}
        />

        <MDBox
          mt={3}
          p={2}
          sx={{ backgroundColor: '#f8f9fa', borderRadius: 1 }}
        >
          <MDBox display="flex" alignItems="flex-start" gap={1.5}>
            <Checkbox
              checked={sendAcknowledgment}
              onChange={(e) => setSendAcknowledgment(e.target.checked)}
              disabled={isLoading}
              sx={{ padding: '6px 8px 6px 6px', marginTop: '2px' }}
            />
            <MDBox flex={1}>
              <MDTypography variant="body2" fontWeight="bold">
                Send customer acknowledgment
              </MDTypography>
              <MDTypography variant="caption" color="text.secondary">
                Notify the customer about this change via{' '}
                {getAcknowledgmentMethod()}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </MDBox>
    </Modal>
  );
};

// Clickable Cell Component - just shows value and opens modal on click
const ClickableCell = ({
  value,
  onClick,
  type = 'number',
  prefix = '',
}: {
  value: number | string | null;
  onClick: () => void;
  type?: 'number' | 'text' | 'date';
  prefix?: string;
}) => {
  const formatValue = (val: number | string | null) => {
    if (type === 'number' && val !== null && val !== '') {
      const numVal = typeof val === 'string' ? parseFloat(val) : val;
      return !isNaN(numVal) ? numVal.toFixed(2) : val?.toString() || '';
    }
    if (type === 'date' && val) {
      return new Date(val + 'T12:00:00Z').toLocaleDateString();
    }
    return val?.toString() || '';
  };

  return (
    <MDBox
      display="flex"
      alignItems="center"
      sx={{
        cursor: 'pointer',
        padding: '4px 8px',
        borderRadius: '4px',
        minHeight: '24px',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          border: '1px solid rgba(25, 118, 210, 0.3)',
        },
        border: '1px solid transparent',
      }}
      onClick={onClick}
    >
      <MDTypography variant="caption">
        {value ? `${prefix}${formatValue(value)}` : '-'}
      </MDTypography>
    </MDBox>
  );
};

const SalesOrderView: React.FC<Props> = ({
  salesOrder,
  editable = false,
  onUpdate,
}) => {
  const [showStagedPurchaseOrderModal, setShowStagedPurchaseOrderModal] =
    useState(false);
  const [isErrorExpanded, setIsErrorExpanded] = useState(false);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingField, setEditingField] = useState<{
    lineItem: SalesOrderLineItem;
    field: 'quantity' | 'unitPrice' | 'rescheduledDate';
    currentValue: string | number | null;
  } | null>(null);

  const [retryEdiAckResult, setRetryEdiAckResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const salesOrderDraftId = salesOrder.stagedCustomerPurchaseOrderId;

  const updateSalesOrderLineItem = useUpdateSalesOrderLineItem(
    salesOrder.id,
    () => {
      console.log('Update successful');
      onUpdate?.();
      setShowEditModal(false);
      setEditingField(null);
    },
  );

  const retryEdiAckMutation = useRetrySendEdiAck(salesOrder.id, (response) => {
    setRetryEdiAckResult(response);
  });

  const handleRetrySendEdiAck = () => {
    setRetryEdiAckResult(null);
    retryEdiAckMutation.mutate();
  };

  const handleFieldClick = (
    lineItem: SalesOrderLineItem,
    field: 'quantity' | 'unitPrice' | 'rescheduledDate',
    currentValue: string | number | null,
  ) => {
    setEditingField({ lineItem, field, currentValue });
    setShowEditModal(true);
  };

  const handleEditConfirm = (
    value: string | number,
    sendAcknowledgment: boolean,
  ) => {
    if (!editingField) return;

    console.log('Making API call with:', {
      lineItemId: editingField.lineItem.id,
      field: editingField.field,
      value,
      sendAcknowledgment,
    });

    updateSalesOrderLineItem.mutate(
      {
        lineItemId: editingField.lineItem.id,
        field: editingField.field,
        value,
        sendAcknowledgment,
      },
      {
        onError: (error) => {
          console.error('Failed to update sales order line item:', error);
          // Don't close the modal on error, let the user see the error and try again
        },
      },
    );
  };

  const handleEditCancel = () => {
    setShowEditModal(false);
    setEditingField(null);
  };

  const lineItemColumns: ColumnDef<SalesOrderLineItem>[] = [
    {
      accessorKey: 'productInfo',
      header: 'Product',
      cell: (info) => {
        const lineItem = info.row.original;

        // Handle customer purchase order line item type
        if (
          lineItem.salesOrderItemType === 'customer_purchase_order_line_item' &&
          lineItem.customerPurchaseOrderLineItemId
        ) {
          return (
            <MDBox>
              <MDTypography variant="caption" color="text.secondary">
                Product from PO Line Item
              </MDTypography>
            </MDBox>
          );
        }

        // Handle direct entry type with product info
        if (lineItem.productId && lineItem.productVariantId) {
          return (
            <ProductV2Pill
              productId={lineItem.productId}
              productVariantId={lineItem.productVariantId}
            />
          );
        }

        return (
          <MDTypography variant="caption" color="text.secondary">
            Product info not available
          </MDTypography>
        );
      },
      minSize: 200,
    },
    {
      accessorKey: 'quantity',
      header: 'Quantity',
      cell: (info) => {
        const value = info.getValue() as number | null;
        const lineItem = info.row.original;

        if (editable) {
          return (
            <ClickableCell
              value={value}
              onClick={() => handleFieldClick(lineItem, 'quantity', value)}
              type="number"
            />
          );
        }

        return (
          <MDTypography variant="caption">
            {value !== null ? value.toString() : '-'}
          </MDTypography>
        );
      },
      size: 100,
    },
    {
      accessorKey: 'unitPrice',
      header: 'Price / Unit',
      cell: (info) => {
        const value = info.getValue() as number | null;
        const lineItem = info.row.original;

        if (editable) {
          return (
            <ClickableCell
              value={value}
              onClick={() => handleFieldClick(lineItem, 'unitPrice', value)}
              type="number"
              prefix="$"
            />
          );
        }

        return value !== null ? (
          <MDTypography variant="caption">
            $
            {new Intl.NumberFormat('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(value)}
          </MDTypography>
        ) : (
          <MDTypography variant="caption" color="text.secondary">
            -
          </MDTypography>
        );
      },
      size: 120,
    },
    {
      accessorKey: 'lineTotal',
      header: 'Total',
      cell: (info) => {
        const value = info.getValue() as number;
        return (
          <MDTypography variant="caption">
            $
            {new Intl.NumberFormat('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(value)}
          </MDTypography>
        );
      },
      size: 120,
    },
    {
      accessorKey: 'rescheduledDate',
      header: 'Delivery Date',
      cell: (info) => {
        const value = info.getValue() as string | null;
        const lineItem = info.row.original;

        if (editable) {
          return (
            <ClickableCell
              value={value}
              onClick={() =>
                handleFieldClick(lineItem, 'rescheduledDate', value)
              }
              type="date"
            />
          );
        }

        return value ? (
          <MDTypography variant="caption">
            {new Date(value).toLocaleDateString()}
          </MDTypography>
        ) : (
          <MDTypography variant="caption" color="text.secondary">
            -
          </MDTypography>
        );
      },
      size: 120,
    },
  ];

  const table = useReactTable({
    data: salesOrder.salesOrderLineItems || [],
    columns: lineItemColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Show Famous sync status view if flag is enabled
  if (SHOW_FAMOUS_SYNC) {
    // Special error view for sync failures
    if (salesOrder.originSyncStatus === 'failed') {
      return (
        <MDBox sx={{ p: 4 }}>
          <MDBox sx={{ maxWidth: '600px', margin: '0 auto' }}>
            <MDBox
              sx={{
                p: 4,
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'error.main',
                backgroundColor: 'background.paper',
                maxWidth: '600px',
                width: '100%',
              }}
            >
              <MDTypography
                variant="h4"
                fontWeight="bold"
                mb={2}
                color="error.main"
              >
                ⚠️ Famous Sync Error
              </MDTypography>

              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={2}
                mb={3}
              >
                <MDBox
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: 'error.main',
                  }}
                />
                <MDTypography
                  variant="h6"
                  color="error.main"
                  fontWeight="medium"
                >
                  Sync Failed
                </MDTypography>
              </MDBox>

              {salesOrder.originSyncErrorMessage && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  <MDBox
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setIsErrorExpanded(!isErrorExpanded)}
                  >
                    <MDTypography variant="body2">
                      <strong>Error Details</strong>{' '}
                      {isErrorExpanded
                        ? '(click to collapse)'
                        : '(click to expand)'}
                    </MDTypography>
                    <IconButton
                      size="small"
                      sx={{
                        transform: isErrorExpanded
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)',
                        transition: 'transform 0.3s ease',
                        p: 0.5,
                      }}
                    >
                      <ExpandMoreIcon fontSize="small" />
                    </IconButton>
                  </MDBox>
                  <Collapse in={isErrorExpanded} timeout={300}>
                    <MDTypography
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                        mt: 1,
                        p: 1,
                        borderRadius: 1,
                      }}
                    >
                      {salesOrder.originSyncErrorMessage}
                    </MDTypography>
                  </Collapse>
                </Alert>
              )}

              <MDBox
                sx={{
                  p: 3,
                  backgroundColor: '#fff3cd',
                  borderRadius: 1,
                  border: '1px solid #ffeaa7',
                  mb: 3,
                }}
              >
                <MDTypography variant="h6" fontWeight="bold" mb={2}>
                  🆘 Support Required
                </MDTypography>
                <MDTypography variant="body1" mb={2}>
                  There was an error syncing this sales order with Famous.
                  Please contact our support team immediately:
                </MDTypography>

                <MDBox
                  sx={{
                    p: 2,
                    backgroundColor: '#f8f9fa',
                    borderRadius: 1,
                    border: '1px solid #e1e8ed',
                    textAlign: 'left',
                  }}
                >
                  <MDTypography variant="body2" fontWeight="bold" mb={1}>
                    📧 Email: support@gently-ai.com
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="bold">
                    📞 Phone/text: 646-657-5539
                  </MDTypography>
                </MDBox>

                <MDTypography
                  variant="body2"
                  mt={2}
                  sx={{ fontStyle: 'italic' }}
                >
                  Please include the sales order ID #{salesOrder.internalId} and
                  share the link to this page when contacting support.
                </MDTypography>
              </MDBox>

              {/* Order Summary for reference */}
              <MDBox textAlign="left">
                <MDTypography variant="h6" fontWeight="bold" mb={2}>
                  Order Reference
                </MDTypography>
                <MDBox
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '140px 1fr',
                    gap: 1,
                    '& > .label': {
                      fontWeight: 'bold',
                      fontSize: 14,
                    },
                  }}
                >
                  <MDTypography className="label">Order ID:</MDTypography>
                  <MDTypography variant="body2">
                    {salesOrder.originId ? `#${salesOrder.originId}` : 'N/A'}
                  </MDTypography>

                  <MDTypography className="label">Customer:</MDTypography>
                  <MDTypography variant="body2">
                    {salesOrder.customer?.name || 'Not specified'}
                  </MDTypography>

                  {/* <MDTypography className="label">Total:</MDTypography>
                  <MDTypography variant="body2">
                    {salesOrder.totalAmount !== null
                      ? `$${new Intl.NumberFormat('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(salesOrder.totalAmount)}`
                      : 'Not calculated'}
                  </MDTypography> */}
                </MDBox>
              </MDBox>
            </MDBox>
          </MDBox>
        </MDBox>
      );
    }

    // Normal sync status view for success/pending/other states
    const getSyncStatusColor = () => {
      switch (salesOrder.originSyncStatus) {
        case 'success':
          return 'success.main';
        case 'pending':
          return 'warning.main';
        default:
          return 'text.secondary';
      }
    };

    const getSyncStatusLabel = () => {
      switch (salesOrder.originSyncStatus) {
        case 'success':
          return 'Successfully Synced';
        case 'pending':
          return 'Sync Pending';
        default:
          return 'Not Synced';
      }
    };

    return (
      <MDBox sx={{ display: 'flex', flexDirection: 'column' }}>
        <MDBox
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            textAlign: 'center',
            p: 4,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {/* Famous Sync Status */}
          <MDBox
            sx={{
              p: 4,
              borderRadius: 2,
              border: '2px solid',
              borderColor: getSyncStatusColor(),
              backgroundColor: 'background.paper',
              maxWidth: '600px',
              width: '100%',
            }}
          >
            <MDTypography variant="h4" fontWeight="bold" mb={2}>
              Famous Integration Status
            </MDTypography>

            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={2}
              mb={3}
            >
              <MDBox
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: getSyncStatusColor(),
                }}
              />
              <MDTypography
                variant="h6"
                sx={{ color: getSyncStatusColor() }}
                fontWeight="medium"
              >
                {getSyncStatusLabel()}
              </MDTypography>
            </MDBox>

            <MDBox
              sx={{
                p: 3,
                backgroundColor: '#f8f9fa',
                borderRadius: 1,
                border: '1px solid #e1e8ed',
              }}
            >
              <MDTypography variant="h6" fontWeight="bold" mb={2}>
                📋 Action Required
              </MDTypography>
              <MDTypography variant="body1" mb={2}>
                Please check Famous to confirm that this sales order was created
                properly:
              </MDTypography>
              <MDBox component="ul" sx={{ pl: 3, mb: 2 }}>
                <MDBox component="li" mb={1}>
                  <MDTypography variant="body2">
                    Verify all line items are present with correct quantities
                    and prices
                  </MDTypography>
                </MDBox>
                <MDBox component="li" mb={1}>
                  <MDTypography variant="body2">
                    Confirm customer information is accurate
                  </MDTypography>
                </MDBox>
                <MDBox component="li" mb={1}>
                  <MDTypography variant="body2">
                    Check shipping and delivery dates
                  </MDTypography>
                </MDBox>
                <MDBox component="li">
                  <MDTypography variant="body2">
                    Anything else you manually input into the order
                  </MDTypography>
                </MDBox>
              </MDBox>

              {salesOrder.originSyncStatus === 'success' && (
                <MDBox
                  sx={{
                    p: 2,
                    backgroundColor: '#e8f5e9',
                    borderRadius: 1,
                    border: '1px solid #4caf50',
                  }}
                >
                  <MDTypography variant="body2" color="success.dark">
                    ✅ Order has been successfully synchronized with Famous
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>

            {/* Sales Order Details Summary */}
            <MDBox mt={4} textAlign="left">
              <MDTypography variant="h6" fontWeight="bold" mb={2}>
                Order Summary
              </MDTypography>
              <MDBox
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '140px 1fr',
                  gap: 1,
                  '& > .label': {
                    fontWeight: 'bold',
                    fontSize: 14,
                  },
                }}
              >
                <MDTypography className="label">Gently Order ID:</MDTypography>
                <MDTypography variant="body2">
                  #{salesOrder.internalId}
                </MDTypography>

                <MDTypography className="label">Famous Order ID:</MDTypography>
                <MDTypography variant="body2">
                  <b>{salesOrder.originId || 'N/A'}</b>
                </MDTypography>

                <MDTypography className="label">Customer:</MDTypography>
                <MDTypography variant="body2">
                  {salesOrder.customer?.name || 'Not specified'}
                </MDTypography>

                {/* <MDTypography className="label">Total:</MDTypography>
                <MDTypography variant="body2">
                  {salesOrder.totalAmount !== null
                    ? `$${new Intl.NumberFormat('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(salesOrder.totalAmount)}`
                    : 'Not calculated'}
                </MDTypography> */}
              </MDBox>
            </MDBox>
          </MDBox>
        </MDBox>
      </MDBox>
    );
  }

  return (
    <MDBox sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Header section with key information */}
      <MDBox mb={3} sx={{ flexShrink: 0 }}>
        {salesOrderDraftId && (
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDTypography
              sx={{
                fontWeight: 'bold',
                fontSize: 14,
                cursor: 'pointer',
                color: 'primary.main',
                textDecoration: 'none',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  color: 'primary.dark',
                },
              }}
              onClick={() => {
                setShowStagedPurchaseOrderModal(true);
              }}
            >
              See original purchase order
            </MDTypography>
          </MDBox>
        )}

        <MDBox
          sx={{
            display: 'grid',
            gridTemplateColumns: '140px 1fr',
            gap: 1,
            '& > .label': {
              fontWeight: 'bold',
              fontSize: 14,
              display: 'flex',
              alignItems: 'center',
            },
          }}
        >
          <MDTypography className="label">Customer:</MDTypography>
          {salesOrder.customer ? (
            <CustomerPill
              customer={{
                name: salesOrder.customer.name,
                emailDomain: salesOrder.customer.emailDomain ?? undefined,
                phoneNumber: salesOrder.customer.phoneNumber ?? undefined,
              }}
            />
          ) : (
            <MDTypography variant="body2" color="text.secondary">
              Not specified
            </MDTypography>
          )}

          {salesOrder.customerPurchaseOrderNumber && (
            <>
              <MDTypography className="label">PO Number:</MDTypography>
              <div className="flex items-center gap-1.5">
                <EnvelopeSimple className="size-4 text-muted-foreground" />
                <Badge variant="outline">
                  {salesOrder.customerPurchaseOrderNumber}
                </Badge>
              </div>
            </>
          )}

          <MDTypography className="label">Ship To Location:</MDTypography>
          <MDTypography variant="body2">
            {salesOrder.shipToLocation?.address}
          </MDTypography>

          <MDTypography className="label">Order Date:</MDTypography>
          <MDTypography variant="body2">
            {new Date(salesOrder.orderDate).toLocaleDateString()}
          </MDTypography>

          <MDTypography className="label">Delivery Date:</MDTypography>
          <MDTypography variant="body2">
            {salesOrder.deliveryDate
              ? new Date(salesOrder.deliveryDate).toLocaleDateString()
              : 'Not specified'}
          </MDTypography>

          <MDTypography className="label">Status:</MDTypography>
          <MDTypography variant="body2" sx={{ textTransform: 'capitalize' }}>
            {salesOrder.status.replace('_', ' ')}
          </MDTypography>

          <MDTypography className="label">Total:</MDTypography>
          <MDTypography variant="body2">
            {salesOrder.totalAmount !== null
              ? `$${new Intl.NumberFormat('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(salesOrder?.totalAmount ?? 0)}`
              : 'Not calculated'}
          </MDTypography>
        </MDBox>
      </MDBox>

      {/* Line items table */}
      <MDBox sx={{ flex: 1, minHeight: 0 }}>
        <MDBox
          mb={2}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'grey.300',
            pb: 1,
          }}
        >
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6" fontWeight="bold">
              Line Items ({salesOrder.salesOrderLineItems?.length || 0})
            </MDTypography>
          </MDBox>
        </MDBox>

        <TableContainer sx={{ boxShadow: 'none' }}>
          <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow>
                {table.getHeaderGroups().map((headerGroup) =>
                  headerGroup.headers.map((header, index) => {
                    // Define percentage widths for each column to match SalesOrderDraftsTable pattern
                    const columnWidths = ['35%', '15%', '20%', '15%', '15%'];

                    return (
                      <TableCell
                        key={header.id}
                        sx={{
                          position: 'relative',
                          textAlign: 'left',
                          opacity: 0.7,
                          background: 'transparent',
                          color: 'rgb(123, 128, 154)',
                          boxShadow: 'none',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          whiteSpace: 'nowrap',
                          width: columnWidths[index],
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : header.column.columnDef.header instanceof Function
                            ? header.column.columnDef.header(
                                header.getContext(),
                              )
                            : header.column.columnDef.header}
                      </TableCell>
                    );
                  }),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row) => {
                  const isRejected = row.original.ediRejected;
                  return (
                    <TableRow
                      key={row.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                        ...(isRejected && {
                          opacity: 0.6,
                          textDecoration: 'line-through',
                          backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        }),
                      }}
                    >
                      {row.getVisibleCells().map((cell, index) => {
                        const columnWidths = [
                          '35%',
                          '15%',
                          '20%',
                          '15%',
                          '15%',
                        ];

                        return (
                          <TableCell
                            key={cell.id}
                            sx={{
                              color: '#7b809a',
                              fontSize: '0.875rem',
                              fontWeight: '400',
                              width: columnWidths[index],
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              ...(isRejected && {
                                textDecoration: 'line-through',
                                opacity: 0.7,
                              }),
                            }}
                          >
                            {cell.column.columnDef.cell instanceof Function
                              ? cell.column.columnDef.cell(cell.getContext())
                              : String(cell.getValue() ?? '')}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={lineItemColumns.length}
                    sx={{
                      textAlign: 'center',
                      py: 4,
                      color: 'text.secondary',
                    }}
                  >
                    <MDTypography variant="body2">
                      No line items found
                    </MDTypography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>

      {showStagedPurchaseOrderModal && salesOrderDraftId && (
        <SalesOrderDraftModal
          salesOrderDraftId={salesOrderDraftId}
          onClose={() => setShowStagedPurchaseOrderModal(false)}
          editable={false}
          isLoading={false}
        />
      )}

      {showEditModal && editingField && (
        <EditFieldModal
          open={showEditModal}
          onClose={handleEditCancel}
          onConfirm={handleEditConfirm}
          lineItem={editingField.lineItem}
          field={editingField.field}
          currentValue={editingField.currentValue}
          isLoading={updateSalesOrderLineItem.isLoading}
          error={updateSalesOrderLineItem.error}
          salesOrder={salesOrder}
        />
      )}
    </MDBox>
  );
};

export default SalesOrderView;
